import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, useCallback, useContext } from 'react';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import Stack from 'design-system/components/Stack';
import theme from 'design-system/theme';
import FormError from 'inquiry/components/FormError';
import { containerStyles, labelSSTStyles, materialContainerStyles } from 'inquiry/mixins/form';
import { colorCss, fontWeightCss, sizeCss } from 'lib/inquiryStyles';
import { InputStyles } from 'lib/interfaces/input-styles';
import { InputRadioGroupComponent } from 'lib/interfaces/ui-components';
import { fontFamilyCss } from 'verify/lib/utils/font-family';

const Container = styled('div')`
  ${containerStyles}
`;

const MaterialContainer = styled('div')`
  ${materialContainerStyles}

  // since the label does not need to float upwards, override default padding
  padding-top: ${Spacing[2]};
`;

const ItemContainer = styled('fieldset')`
  display: flex;
  flex-direction: column;
`;

const Legend = styled(Markdown).attrs(() => ({ wrapper: 'legend' }))<{
  styles?: InputRadioGroupComponent['styles'];
}>`
  ${({ styles }) => labelSSTStyles(styles)}
`;

const LineItem = styled('label')<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  align-items: baseline;
  margin: 0 ${Spacing[2]};

  & + & {
    margin-top: ${Spacing[2]};
  }
`;

const RadioStyled = styled('input')`
  margin-top: -1px;
  margin-right: 0.5em;
`;

// 'p' and not 'label' since <LineItem/> is already a label
const InputRadioGroupOptionLabel = styled(Markdown).attrs(() => ({ wrapper: 'p' }))<{
  styles: InputRadioGroupComponent['styles'];
  disabled: boolean;
}>`
  /**
   * Elsewhere, Markdown adds significant spacing between paragraphs / newlines.
   * Newlines in checkboxes shouldn't be treated as independent paragraphs.
   **/
  p + p {
    margin: 0 !important;
  }
  /* TODO [strict-migration] The following is not typed properly; please fix! */
  ${({ styles, disabled }) => ({
    display: 'inline-block',
    marginBottom: 0,
    // Text styles
    fontFamily: fontFamilyCss(styles?.['font-family'].base.base) as any,
    fontSize: sizeCss(styles?.['font-size'].base.base) as any,
    fontWeight: fontWeightCss(styles?.['font-weight'].base.base) as any,
    letterSpacing: sizeCss(styles?.['letter-spacing'].base.base) as any,
    lineHeight: sizeCss(styles?.['line-height'].base.base) as any,
    color: disabled
      ? theme.color.text.disabled
      : (colorCss(styles?.['text-color'].base.base) as any),

    a: {
      color: colorCss(styles?.['text-color-highlight'].base.base) as any,
      outlineColor: colorCss(styles?.['text-color-highlight'].base.base) as any,
    },
  })}
`;

const InputRadioGroupOptionDescription = styled(Markdown).attrs(() => ({ wrapper: 'p' }))<{
  styles: InputRadioGroupComponent['styles'];
  disabled: boolean;
}>`
  /**
   * Elsewhere, Markdown adds significant spacing between paragraphs / newlines.
   * Newlines in checkboxes shouldn't be treated as independent paragraphs.
   **/
  p + p {
    margin: 0 !important;
  }
  ${({ styles, disabled }) =>
    /* TODO [strict-migration] The following is not typed properly; please fix! */
    ({
      display: 'inline-block',
      marginBottom: 0,
      // Text styles
      fontFamily: fontFamilyCss(styles?.['font-family']['description'].base) as any,
      fontSize: sizeCss(styles?.['font-size']['description'].base) as any,
      fontWeight: fontWeightCss(styles?.['font-weight']['description'].base) as any,
      letterSpacing: sizeCss(styles?.['letter-spacing']['description'].base) as any,
      lineHeight: sizeCss(styles?.['line-height']['description'].base) as any,
      color: disabled
        ? theme.color.text.disabled
        : (colorCss(styles?.['text-color']['description'].base) as any),

      a: {
        color: colorCss(styles?.['text-color-highlight'].base.base) as any,
        outlineColor: colorCss(styles?.['text-color-highlight'].base.base) as any,
      },
    })}
`;

export interface ThemedFormRadioGroupProps {
  id: string;
  disabled?: boolean;
  options: Array<{ value: string; text: string; 'description-text'?: string }>;
  label?: string;
  value: string;
  styles?: InputRadioGroupComponent['styles'];
}

const ThemedFormRadioGroup = (props: WrappedFieldProps & ThemedFormRadioGroupProps) => {
  const {
    id,
    disabled,
    options,
    input,
    meta: { error, submitFailed },
    label,
    styles,
    ...passThroughProps
  } = props;

  const { inputStyle } = useContext(ThemeContext);
  const hasError = !!(error && submitFailed);

  // Pretend the full radio group is a single input component
  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      input.onChange(evt.currentTarget.value);
    },
    [input.onChange],
  );

  const renderInner = () => (
    <>
      <ItemContainer
        id={id}
        aria-invalid={hasError}
        aria-errormessage={`${id}--error`}
        aria-describedby={`${id}--error`}
      >
        {label && <Legend styles={styles}>{label}</Legend>}
        {options.map(({ text, value: itemValue, 'description-text': descriptionText }, index) => {
          const id = `${input.name}_${index}`;
          const isSelected = input.value === itemValue.toString();

          return (
            <LineItem isSelected={isSelected} htmlFor={id} key={itemValue}>
              <RadioStyled
                {...input}
                {...passThroughProps}
                id={id}
                aria-label={text}
                aria-describedby={descriptionText ? `${id}-description` : undefined}
                disabled={disabled}
                onChange={handleChange}
                type='radio'
                value={itemValue}
                checked={itemValue === input.value}
                aria-invalid={hasError}
              />
              <Stack spacing={Spacing[0]}>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <InputRadioGroupOptionLabel styles={styles} disabled={disabled}>
                  {text}
                </InputRadioGroupOptionLabel>
                {descriptionText && (
                  <InputRadioGroupOptionDescription
                    id={`${id}-description`}
                    styles={styles}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    disabled={disabled}
                  >
                    {descriptionText}
                  </InputRadioGroupOptionDescription>
                )}
              </Stack>
            </LineItem>
          );
        })}
      </ItemContainer>
      <FormError id={`${id}--error`} hasError={hasError} errorMessage={error} styles={styles} />
    </>
  );

  switch (inputStyle) {
    case InputStyles.Material:
      return <MaterialContainer>{renderInner()}</MaterialContainer>;
    case InputStyles.Outlined:
    default:
      return <Container>{renderInner()}</Container>;
  }
};

export default ThemedFormRadioGroup;
